import { getGuestRequestFailureDiagnostics } from './failureDiagnostics';
import { getBreadcrumbsSnapshot } from './breadcrumbs';
import { computeClientFingerprint } from './fingerprints';
import {
  getBuildId,
  getDeviceSnapshot,
  getEnvironment,
  getOrCreateSessionId,
  getRoutePathname,
  isTelemetryEnabled,
} from './sessionContext';
import {
  normalizeError,
  sanitizeContext,
  sanitizeNetwork,
  sanitizeSupabaseError,
} from './sanitize';
import type { ClientErrorPayload, ErrorReportInput, IdentitySnapshot } from './types';

const DEDUPE_WINDOW_MS = 60_000;
const MAX_IN_FLIGHT = 3;
const FLUSH_DEBOUNCE_MS = 2_000;

// Match the runbook's diagnostic traffic marker, never an authorization rule.
const CRAWLER_USER_AGENT_PATTERN =
  /(bot|crawler|spider|facebookexternalhit|meta-external|mediapartners-google|google-inspectiontool|googleother|apis-google|feedfetcher-google|google-favicon)/i;
const SCRIPT_LOAD_TARGET_KINDS: ReadonlySet<unknown> = new Set([
  'script',
  'modulepreload',
  'script_preload',
]);
const OPTIONAL_CRAWLER_SCRIPT_HOSTS = new Set([
  'fundingchoicesmessages.google.com',
  'pagead2.googlesyndication.com',
  'www.googletagmanager.com',
  'googleads.g.doubleclick.net',
]);

let identitySnapshot: IdentitySnapshot = {
  userId: null,
  anonymousId: null,
  isAnonymous: true,
};

const recentFingerprints = new Map<string, number>();
const queue: ClientErrorPayload[] = [];
let inFlight = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export function setErrorReporterIdentity(snapshot: IdentitySnapshot): void {
  identitySnapshot = snapshot;
}

export function reportError(input: ErrorReportInput): void {
  if (!isTelemetryEnabled()) return;

  try {
    const normalized = normalizeError(input.error);
    const network = sanitizeNetwork(input.network ?? {});
    const device = getDeviceSnapshot();
    const requestedSeverity = input.severity ?? 'error';
    // Optional advertising/measurement loads do not gate application playback.
    // Keep the crawler diagnostic, but separate it from player action failures.
    // Runtime, opaque, CSP, first-party, auth and fatal errors keep their severity.
    const severity =
      requestedSeverity === 'error' &&
      input.source === 'window.onerror' &&
      normalized.message === 'Script resource failed to load' &&
      input.context?.scriptAttribution === 'known_external_google' &&
      SCRIPT_LOAD_TARGET_KINDS.has(input.context?.resourceTargetKind) &&
      typeof network?.host === 'string' &&
      OPTIONAL_CRAWLER_SCRIPT_HOSTS.has(network.host) &&
      typeof device.userAgent === 'string' &&
      CRAWLER_USER_AGENT_PATTERN.test(device.userAgent)
        ? 'warning'
        : requestedSeverity;
    const fingerprint = computeClientFingerprint(
      input.source,
      normalized.name,
      normalized.message,
      {
        severity,
        host: network?.host,
        path: network?.path,
      }
    );
    const now = Date.now();
    const lastSent = recentFingerprints.get(fingerprint);
    if (lastSent && now - lastSent < DEDUPE_WINDOW_MS) {
      return;
    }
    recentFingerprints.set(fingerprint, now);

    const payload: ClientErrorPayload = {
      session_id: getOrCreateSessionId(),
      fingerprint,
      severity,
      source: input.source,
      message: normalized.message,
      stack: normalized.stack,
      name: normalized.name,
      user_id: identitySnapshot.userId ?? undefined,
      anonymous_id: identitySnapshot.anonymousId ?? undefined,
      is_anonymous: identitySnapshot.isAnonymous,
      route: getRoutePathname(),
      build_id: getBuildId(),
      environment: getEnvironment(),
      context: sanitizeContext({
        ...getGuestRequestFailureDiagnostics(input.supabaseError ?? input.error),
        ...input.context,
      }),
      breadcrumbs: getBreadcrumbsSnapshot(),
      supabase_error: sanitizeSupabaseError(input.supabaseError ?? undefined),
      network,
      device,
      react: input.react,
    };

    queue.push(payload);
    scheduleFlush();
  } catch {
    // Fail open for UX — never throw from reporting.
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_DEBOUNCE_MS);
}

async function flushQueue(): Promise<void> {
  while (queue.length > 0 && inFlight < MAX_IN_FLIGHT) {
    const payload = queue.shift();
    if (!payload) return;

    inFlight += 1;
    try {
      const { submitClientError } = await import('@/lib/services/errorTelemetry');
      await submitClientError(payload);
    } catch {
      // Swallow transport failures.
    } finally {
      inFlight -= 1;
    }
  }

  if (queue.length > 0) {
    scheduleFlush();
  }
}

export function flushErrorReporter(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  void flushQueue();
}

export function resetErrorReporterForTests(): void {
  queue.length = 0;
  inFlight = 0;
  recentFingerprints.clear();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flushErrorReporter());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushErrorReporter();
    }
  });
}
